import React, { useEffect, useState } from 'react';

import { EmailRounded } from '@mui/icons-material';
import PhoneRounded from '@mui/icons-material/PhoneRounded';
import {
  Box,
  ListItemIcon,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { GridRenderCellParams, useGridApiRef } from '@mui/x-data-grid';
import { gridFilteredSortedRowIdsSelector } from '@mui/x-data-grid/hooks/features/filter/gridFilterSelector';

import PageContent from '../../components/PageContent';
import StudentStateChip from '../../components/StudentStateChip';
import UserDisabledStateChip from '../../components/UserDisabledStateChip';
import UsersList from '../../components/UsersList';
import { useGetStudentsQuery } from '../../services/students';
import { ShortStudent } from '../../services/students/types';
import { StudentState } from '../../types/common';
import {
  STUDENT_STATES,
} from '../../utils/common/constants';
import useSearchStr from '../../utils/react/hooks/useSearchStr';

import {
  StudentStateToggle, StudentStateToggleBar,
} from './index.styles';
import StudentDetails from './StudentDetails';

const AdminStudentsPage = () => {
  const theme = useTheme();
  const gridApiRef = useGridApiRef();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  /**
   * States
   */
  // Fetch student in each state apart from Lead, Graduated & Archived by default
  const [stateToggleStatus, setStateToggleStatus] = useState<{ [key: string]: boolean }>(
    {
      ...Object.fromEntries(Object.keys(STUDENT_STATES).map((state) => [state, true])),
      ...(isMd ? {
        [StudentState.Lead]: false,
        [StudentState.Graduated]: false,
        [StudentState.Archived]: false,
      } : {}),
    },
  );
  const [allToggled, setAllToggled] = useState<boolean>(!isMd);
  const [students, setStudents] = useState<ShortStudent[]>([]);
  const [searchStr, setSearchStr] = useSearchStr('');

  /**
   * Students Queries & Mutations
   */
  const { data: leadStudents,
    isLoading: isLeadStudentsLoading } = useGetStudentsQuery({
    state: StudentState.Lead,
    ...(searchStr ? { search: searchStr } : {}),
  }, { skip: !stateToggleStatus[StudentState.Lead] });
  const { data: enrolledStudents,
    isLoading: isEnrolledStudentsLoading } = useGetStudentsQuery({
    state: StudentState.Enrolled,
    ...(searchStr ? { search: searchStr } : {}),
  }, { skip: !stateToggleStatus[StudentState.Enrolled] });
  const { data: pendingAttendanceStudents,
    isLoading: isPendingAttendanceStudentsLoading } = useGetStudentsQuery({
    state: StudentState.PendingAttendance,
    ...(searchStr ? { search: searchStr } : {}),
  }, { skip: !stateToggleStatus[StudentState.PendingAttendance] });
  const { data: onTrialStudents,
    isLoading: isOnTrialStudentsLoading } = useGetStudentsQuery({
    state: StudentState.OnTrial,
    ...(searchStr ? { search: searchStr } : {}),
  }, { skip: !stateToggleStatus[StudentState.OnTrial] });
  const { data: activeStudents,
    isLoading: isActiveStudentsLoading } = useGetStudentsQuery({
    state: StudentState.Active,
    ...(searchStr ? { search: searchStr } : {}),
  }, { skip: !stateToggleStatus[StudentState.Active] });
  const { data: graduatedStudents,
    isLoading: isGraduatedStudentsLoading } = useGetStudentsQuery({
    state: StudentState.Graduated,
    ...(searchStr ? { search: searchStr } : {}),
  }, { skip: !stateToggleStatus[StudentState.Graduated] });
  const { data: archivedStudents,
    isLoading: isArchivedStudentsLoading } = useGetStudentsQuery({
    state: StudentState.Archived,
    ...(searchStr ? { search: searchStr } : {}),
  }, { skip: !stateToggleStatus[StudentState.Archived] });

  /**
   * Student Population
   */
  useEffect(() => {
    const newStudents = [
      ...(stateToggleStatus[StudentState.Lead]
        ? leadStudents?.data || [] : []),
      ...(stateToggleStatus[StudentState.Enrolled]
        ? enrolledStudents?.data || [] : []),
      ...(stateToggleStatus[StudentState.PendingAttendance]
        ? pendingAttendanceStudents?.data || [] : []),
      ...(stateToggleStatus[StudentState.OnTrial]
        ? onTrialStudents?.data || [] : []),
      ...(stateToggleStatus[StudentState.Active]
        ? activeStudents?.data || [] : []),
      ...(stateToggleStatus[StudentState.Graduated]
        ? graduatedStudents?.data || [] : []),
      ...(stateToggleStatus[StudentState.Archived]
        ? archivedStudents?.data || [] : []),
    ];

    setStudents(newStudents);
  }, [
    leadStudents,
    enrolledStudents,
    pendingAttendanceStudents,
    onTrialStudents,
    activeStudents,
    graduatedStudents,
    archivedStudents,
    stateToggleStatus,
  ]);

  /**
   * Copy values from (filtered and sorted) table to clipboard
   */

  const copyVisibleStudentsAttribute = (attribute: keyof ShortStudent) => {
    let visibleStudents: ShortStudent[] = [];

    if (gridApiRef?.current?.state) {
      const visibleRowIds = gridFilteredSortedRowIdsSelector(
        gridApiRef.current.state,
        gridApiRef.current.instanceId,
      );
      visibleStudents = visibleRowIds.map(
        (id) => students.find((student) => student.id === id))
        .filter((x) => x) as ShortStudent[]; // Exclude students that are filtered out
    }

    const copyText = visibleStudents
      .map((student) => student?.[attribute])
      .filter((x) => x) // Only include the attribute if it exists
      .join(',');

    navigator.clipboard.writeText(copyText);
  };

  /**
   * Forms
   */
  const [studentDetails, setStudentDetails] = useState<ShortStudent>();

  /**
   * Toggle Bar
   */
  const toggleState = (targetState: string) => {
    setAllToggled(false);
    if (allToggled) {
      // If the 'All' flag is turned on, turn it off and turn on the target state
      setStateToggleStatus({
        ...Object.fromEntries(
          Object.keys(STUDENT_STATES).map((state) => [state, false]),
        ),
        [targetState]: true,
      });
    } else {
      setStateToggleStatus({
        ...stateToggleStatus,
        [targetState]: !stateToggleStatus[targetState],
      });
    }
  };

  const toggleAll = () => {
    if (allToggled) {
      setStateToggleStatus(
        Object.fromEntries(
          Object.keys(STUDENT_STATES).map((state) => [state, false]),
        ),
      );
      setAllToggled(false);
    } else {
      setStateToggleStatus(
        Object.fromEntries(
          Object.keys(STUDENT_STATES).map((state) => [state, true]),
        ),
      );
      setAllToggled(true);
    }
  };

  /**
   * Render functions
   */
  const renderDisabledState = (params: GridRenderCellParams<any, boolean>) =>
    (<UserDisabledStateChip size="small" disabled={params?.value || false} />);

  const renderStudentState = (params: GridRenderCellParams<any, string>) => (
    <StudentStateChip size="small" state={params.value as StudentState} />
  );

  return (
    <PageContent
      sideBar={studentDetails ? (
        <StudentDetails
          student={studentDetails}
          onClose={() => setStudentDetails(undefined)}
        />
      ) : <div />}
      sideBarOpen={!!studentDetails}
    >
      <StudentStateToggleBar>
        {isMd && (
          <Box
            display="flex"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={2}
            >
              <StudentStateToggle
                $color="primary"
                $selected={allToggled}
                onClick={toggleAll}
              >
                All
              </StudentStateToggle>
              {Object.keys(STUDENT_STATES).map((state) => (
                <StudentStateToggle
                  key={state}
                  $color={STUDENT_STATES?.[state].colorKey}
                  $selected={!allToggled && stateToggleStatus[state]}
                  onClick={() => toggleState(state)}
                >
                  {STUDENT_STATES?.[state].label }
                </StudentStateToggle>
              ))}
            </Stack>
          </Box>
        )}
      </StudentStateToggleBar>
      <UsersList
        columns={[
          {
            field: 'state',
            headerName: 'State',
            width: 170,
            renderCell: renderStudentState,
          },
          {
            field: 'gradYear',
            headerName: 'Grad Year',
            width: 100,
          },
          {
            field: 'school',
            headerName: 'School',
            width: 200,
          },
          {
            field: 'name',
            headerName: 'Name',
            width: 200,
          },
          {
            field: 'disabled',
            headerName: 'State',
            width: 100,
            renderCell: renderDisabledState,
          },
          {
            field: 'mostConsecutiveAbsences',
            headerName: 'Absence Streak',
            width: 150,
          },
        ]}
        data={students}
        selectedRowId={studentDetails?.id}
        onSearch={(s) => setSearchStr(s)}
        loading={
          isLeadStudentsLoading
          || isEnrolledStudentsLoading
          || isPendingAttendanceStudentsLoading
          || isOnTrialStudentsLoading
          || isActiveStudentsLoading
          || isGraduatedStudentsLoading
          || isArchivedStudentsLoading
        }
        onRowClick={(entry) => setStudentDetails(entry.row)}
        addButtonLabel="Add Student"
        apiRef={gridApiRef}
        menuItems={[
          <MenuItem
            key="email"
            onClick={() => copyVisibleStudentsAttribute('email')}
          >
            <ListItemIcon>
              <EmailRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">
              Copy Emails
            </Typography>
          </MenuItem>,
          <MenuItem
            key="parentEmail"
            onClick={() => copyVisibleStudentsAttribute('parentEmail')}
          >
            <ListItemIcon>
              <EmailRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">
              Copy Parent Emails
            </Typography>
          </MenuItem>,
          <MenuItem
            key="phoneNumber"
            onClick={() => copyVisibleStudentsAttribute('phoneNumber')}
          >
            <ListItemIcon>
              <PhoneRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">
              Copy Phone Numbers
            </Typography>
          </MenuItem>,
          <MenuItem
            key="parentPhoneNumber"
            onClick={() => copyVisibleStudentsAttribute('parentPhoneNumber')}
          >
            <ListItemIcon>
              <PhoneRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">
              Copy Parent Phone Numbers
            </Typography>
          </MenuItem>,
        ]}
      />
    </PageContent>
  );
};

export default AdminStudentsPage;
